'use client';

import Link from 'next/link';
import type { RefundTier } from '@/lib/cms/types';
import siteTheme from '@/lib/theme';

const { fonts } = siteTheme;
const navy  = '#0D2F44';
const cream = '#EDECEA';
const gold  = '#B89A5A';

const TIER_COLORS = {
  green: { bg: '#f0faf4', border: '#27ae6033',            text: '#1a6b3a', badge: '#e6f7ef' },
  amber: { bg: '#fffbf0', border: 'rgba(212,160,23,0.3)', text: '#92640a', badge: '#fef9e7' },
  red:   { bg: '#fff5f5', border: 'rgba(192,57,43,0.2)',  text: '#c0392b', badge: '#fdf2f2' },
};

function toINR(n: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

export default function CancellationPolicyClient({
  heroTitle,
  heroSubtitle,
  policyContent,
  refundTiers,
}: {
  heroTitle: string;
  heroSubtitle: string;
  policyContent: string;
  refundTiers: RefundTier[];
}) {
  const sorted = [...refundTiers].sort((a, b) => b.hours_before - a.hours_before);

  return (
    <>
      {/* Hero */}
      <section style={{ position: 'relative', paddingTop: '8rem', paddingBottom: '5rem', backgroundColor: navy, textAlign: 'center', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: "url('https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1600&q=80')", backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
        <div style={{ position: 'relative', zIndex: 1, padding: '0 1.5rem' }}>
          <p style={{ fontFamily: fonts.sans, fontSize: '1.1rem', letterSpacing: '0.4em', color: gold, textTransform: 'uppercase', marginBottom: '0.8rem' }}>Guest Information</p>
          <h1 style={{ fontFamily: fonts.serif, fontSize: 'clamp(2.8rem,5vw,4.5rem)', fontWeight: 400, color: '#fff', margin: '0 0 1rem' }}>{heroTitle}</h1>
          <p style={{ fontFamily: fonts.sans, fontSize: '1.3rem', color: 'rgba(255,255,255,0.65)', margin: 0, maxWidth: '44rem', marginInline: 'auto' }}>{heroSubtitle}</p>
        </div>
      </section>

      <section style={{ backgroundColor: cream, padding: 'clamp(3rem,6vw,6rem) 1.5rem' }}>
        <div style={{ maxWidth: '64rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '3rem' }}>

          {/* Refund schedule */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.6rem', boxShadow: '0 2px 24px rgba(0,0,0,0.08)', padding: 'clamp(2rem,4vw,3rem)' }}>
            <p style={{ fontFamily: fonts.sans, fontSize: '1.05rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(13,47,68,0.4)', margin: '0 0 0.4rem', fontWeight: 600 }}>Refund Schedule</p>
            <h2 style={{ fontFamily: fonts.serif, fontSize: '2.4rem', fontWeight: 400, color: navy, margin: '0 0 2rem' }}>
              How refunds work
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {sorted.map((tier, idx) => {
                const c    = TIER_COLORS[tier.color] ?? TIER_COLORS.green;
                const next = sorted[idx + 1];
                const windowLabel = tier.hours_before === 0
                  ? `Within ${next ? next.hours_before : 0}h of check-in`
                  : next
                    ? `${next.hours_before}h – ${tier.hours_before}h before check-in`
                    : `More than ${tier.hours_before}h before check-in`;

                return (
                  <div key={tier.id} style={{ display: 'flex', alignItems: 'stretch', gap: 0, border: `1px solid ${c.border}`, borderRadius: '0.5rem', overflow: 'hidden' }}>

                    {/* Percent column */}
                    <div style={{ flexShrink: 0, width: '8rem', backgroundColor: c.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.4rem 1rem', borderRight: `1px solid ${c.border}` }}>
                      <p style={{ fontFamily: fonts.serif, fontSize: '2.2rem', fontWeight: 400, color: c.text, margin: 0, lineHeight: 1 }}>{tier.refund_percent}%</p>
                      <p style={{ fontFamily: fonts.sans, fontSize: '0.95rem', color: c.text, margin: '0.3rem 0 0', opacity: 0.75, textAlign: 'center' }}>refund</p>
                    </div>

                    {/* Content column */}
                    <div style={{ flex: 1, padding: '1.2rem 1.6rem', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.3rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: fonts.sans, fontSize: '1.2rem', fontWeight: 600, color: navy }}>{tier.label}</span>
                        <span style={{ fontFamily: fonts.sans, fontSize: '1rem', backgroundColor: c.badge, color: c.text, border: `1px solid ${c.border}`, borderRadius: '2rem', padding: '0.15rem 0.7rem' }}>
                          {windowLabel}
                        </span>
                      </div>
                      {tier.description && (
                        <p style={{ fontFamily: fonts.sans, fontSize: '1.1rem', color: 'rgba(13,47,68,0.6)', margin: 0 }}>{tier.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Example calculation */}
            {sorted.length > 0 && sorted[0].refund_percent === 100 && (
              <div style={{ marginTop: '1.8rem', padding: '1.2rem 1.4rem', backgroundColor: cream, borderRadius: '0.45rem' }}>
                <p style={{ fontFamily: fonts.sans, fontSize: '1.1rem', color: 'rgba(13,47,68,0.55)', margin: 0 }}>
                  <strong style={{ color: navy }}>Example:</strong> If your booking total is {toINR(10000)} and you cancel more than {sorted[0].hours_before} hours before check-in, you receive a full refund of {toINR(10000)}.
                  {sorted.length > 1 && sorted[1].refund_percent > 0 && ` If you cancel between ${sorted[1].hours_before}h and ${sorted[0].hours_before}h before check-in, you receive ${sorted[1].refund_percent}% — ${toINR(10000 * sorted[1].refund_percent / 100)}.`}
                </p>
              </div>
            )}
          </div>

          {/* Policy content from CMS */}
          {policyContent && (
            <div style={{ backgroundColor: '#fff', borderRadius: '0.6rem', boxShadow: '0 2px 24px rgba(0,0,0,0.08)', padding: 'clamp(2rem,4vw,3rem)' }}>
              <h2 style={{ fontFamily: fonts.serif, fontSize: '2.2rem', fontWeight: 400, color: navy, margin: '0 0 1.6rem' }}>Additional Terms</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {policyContent.split('\n').filter(Boolean).map((para, i) => (
                  <p key={i} style={{ fontFamily: fonts.sans, fontSize: '1.2rem', color: 'rgba(13,47,68,0.7)', lineHeight: 1.75, margin: 0 }}>{para}</p>
                ))}
              </div>
            </div>
          )}

          {/* General notes */}
          <div style={{ backgroundColor: '#fff', borderRadius: '0.6rem', boxShadow: '0 2px 24px rgba(0,0,0,0.08)', padding: 'clamp(2rem,4vw,3rem)' }}>
            <h2 style={{ fontFamily: fonts.serif, fontSize: '2.2rem', fontWeight: 400, color: navy, margin: '0 0 1.4rem' }}>Important Notes</h2>
            <ul style={{ margin: 0, paddingLeft: '1.4rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {[
                'Refunds are processed to the original payment method used at the time of booking.',
                'Razorpay/bank refunds typically credit within 5–7 business days depending on your bank.',
                'Cancellations made via email or phone are subject to the same policy timelines.',
                'No-show bookings (failure to check in without prior cancellation) are treated as No Refund.',
                'In cases of resort-initiated cancellations due to force majeure, a full refund will be issued.',
                'For group bookings (3 or more rooms), please contact us directly for the applicable policy.',
              ].map((note, i) => (
                <li key={i} style={{ fontFamily: fonts.sans, fontSize: '1.15rem', color: 'rgba(13,47,68,0.65)', lineHeight: 1.65 }}>{note}</li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div style={{ backgroundColor: navy, borderRadius: '0.6rem', padding: 'clamp(2rem,4vw,3rem)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1.4rem' }}>
            <p style={{ fontFamily: fonts.sans, fontSize: '1.05rem', letterSpacing: '0.4em', color: gold, textTransform: 'uppercase', margin: 0 }}>Need to cancel?</p>
            <h2 style={{ fontFamily: fonts.serif, fontSize: 'clamp(2rem,3.5vw,2.8rem)', fontWeight: 400, color: '#fff', margin: 0 }}>Manage your booking online</h2>
            <p style={{ fontFamily: fonts.sans, fontSize: '1.2rem', color: 'rgba(255,255,255,0.6)', margin: 0, maxWidth: '36rem' }}>
              Look up your reservation, check your refund eligibility, and cancel instantly from My Bookings.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link href="/my-booking" style={{ fontFamily: fonts.sans, fontSize: '1.15rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0.9rem 2.2rem', backgroundColor: gold, color: navy, borderRadius: '0.4rem', textDecoration: 'none' }}>
                My Bookings
              </Link>
              <Link href="/contact" style={{ fontFamily: fonts.sans, fontSize: '1.15rem', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0.9rem 2.2rem', backgroundColor: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '0.4rem', textDecoration: 'none' }}>
                Contact Us
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
